import Lottie from 'lottie-react';
import { useTranslations } from 'next-intl';
import { use, useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Icon, { IconName } from '@/components/ui/Icon';
import { logger } from '@/lib/clientSideLogger';
import mobileHaven from '@/public/lottie/notifications/mobile-haven.json';
import { IosNotificationInstructionsDialogContext } from '../context/IosNotificationInstructionsDialogContext';

export function IosNotificationInstructionsDialog() {
  const t = useTranslations('notifications');
  const { shouldShowIosPopup, closeIosPopup } = use(
    IosNotificationInstructionsDialogContext
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const description = t.raw('dialogs.ios.description');

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (
        !document.fullscreenElement &&
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        !document.webkitFullscreenElement
      ) {
        setIsVideoVisible(false);
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
    };
  }, []);

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (video) {
      // First set the video to visible
      setIsVideoVisible(true);

      // Try to play the video first to ensure user interaction
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.then(
          () => {
            // Video started playing successfully, now enter fullscreen
            if (video.requestFullscreen) {
              video.requestFullscreen().catch((error) => {
                logger('error', error);
              });
              // Safari on iOS
              //eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
            } else if (video.webkitEnterFullscreen) {
              //eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              video.webkitEnterFullscreen();
            }
          },
          (error) => {
            // Video failed to play, log error
            logger('error', error);
          }
        );
      }
    }
  };

  return (
    <Dialog open={shouldShowIosPopup} onOpenChange={closeIosPopup}>
      <DialogContent className='px-4'>
        <DialogHeader className='text-center'>
          <Lottie
            animationData={mobileHaven}
            loop={false}
            className='mx-auto h-[74px] w-[47px]'
          />

          <DialogTitle>{t('dialogs.ios.title')}</DialogTitle>
          <DialogDescription asChild className='my-0'>
            <div className='flex flex-col gap-6 text-center'>
              <ul className='list-none space-y-2'>
                {Array.isArray(description) ? (
                  description.map((item, index) => (
                    <li key={index} className=''>
                      {item}
                      {index === 2 && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src='/images/notifications/ios-share-button.svg'
                          alt='Share button'
                          className='inline w-6'
                        />
                      )}
                    </li>
                  ))
                ) : (
                  <li>{description}</li>
                )}
              </ul>
              <div className='relative'>
                <button
                  onClick={handleVideoClick}
                  className='bg-white-300 relative mx-auto flex h-[123px] w-[192px] items-center justify-center gap-2 overflow-hidden rounded-md transition-colors hover:bg-gray-100'
                >
                  <Icon
                    variant={IconName.Play}
                    className='absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2'
                  />
                  <video
                    ref={videoRef}
                    src='https://rtx04em50g.ufs.sh/f/9IlAiNrHEqvS2BzYrb3Mjl9d0rO4aiu3g7RsSTYK1bZfBUoC'
                    className={`rounded-md transition-opacity duration-300 ${
                      isVideoVisible ? 'opacity-100' : 'opacity-50'
                    }`}
                    playsInline
                    poster='/images/notifications/ios-video-preview.png'
                  />
                </button>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
